"use client";

import React from "react";
import { Loader, CircleQuestionMark, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What is the typical production timeline?",
  answer: "Most cinematic brand films take 4-6 weeks from pre-production to final delivery, ensuring every frame meets our artistry standards."
}, {
  question: "Do you handle social media formatting?",
  answer: "Yes. We deliver master files in 2.35:1 for web and cinematic displays, alongside optimized vertical crops for high-impact social placement."
}, {
  question: "Can we request specific camera gear?",
  answer: "We select equipment based on the project's visual goals, typically utilizing RED or Arri systems to achieve that signature cinematic texture."
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-primary/20 flex items-center justify-center mb-6">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold mb-2 text-foreground uppercase tracking-tighter">
          Transmission Received
        </h3>
        <p className="font-mono text-muted-foreground mb-8 max-w-md text-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-primary/30 hover:bg-primary/10 hover:text-primary font-mono text-xs">
          New Brief
        </Button>
      </div>;
  }
  const inputClasses = "flex h-11 w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-border bg-transparent px-0 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Producer name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Contact Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="ALEX RIVERA" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="ALEX@STUDIO.COM" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone <span className="opacity-50 font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <input type="tel" placeholder="+1 000 000 0000" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Brand / Agency <span className="opacity-50 font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <input type="text" placeholder="PIVOTAL MEDIA" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Project brief is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Project Brief</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your cinematic vision..." className={`${inputClasses} min-h-[120px] resize-none py-3`} {...field} />
            </FormControl>
            <FormDescription className="font-mono text-[10px] uppercase opacity-50">
              Include desired deliverables, timeline, and aesthetic references.
            </FormDescription>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-destructive font-mono text-xs">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-mono uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-none shadow-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing Brief...
          </> : <>
            <Send className="mr-2 size-4" />
            Initiate Project
          </>}
      </Button>
    </Form>;
}
export default function RevampInquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224346" id="secure-your-slot" className="bg-background text-foreground relative py-20 lg:py-32 overflow-hidden">
      {}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-primary/5 rounded-none blur-[120px] pointer-events-none" />
      
      {}
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-secondary/10 rounded-none blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-4 flex flex-col gap-8 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-secondary text-primary border border-border mb-6 text-[10px] font-mono uppercase tracking-widest">
                <CircleQuestionMark className="size-3" />
                <span>Production FAQ</span>
              </div>
              <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tighter leading-none mb-4 text-foreground uppercase">
                The Process Behind The Lens
              </h2>
              <p className="font-mono text-muted-foreground text-sm leading-relaxed">
                Technical specifications and logistical details for brands that demand cinematic artistry and absolute precision.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                  <AccordionTrigger className="text-left font-default text-lg font-semibold uppercase tracking-tight hover:text-primary transition-colors py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-mono text-muted-foreground text-xs leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="border-border bg-card/40 backdrop-blur-sm rounded-none shadow-none overflow-hidden relative">
              {}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-primary opacity-30" />
              
              <CardHeader className="pb-8 pt-10 px-8 md:px-10">
                <CardTitle className="font-default text-3xl md:text-4xl font-bold uppercase tracking-tighter">
                  Secure Your Slot
                </CardTitle>
                <CardDescription className="font-mono text-xs uppercase tracking-widest mt-2 text-muted-foreground">
                  Submit your project requirements for a cinematic consultation.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="px-8 md:px-10 pb-10">
                <FormProvider formKey="secure_slot_form_start_project" formId="secure_slot_form_start_project" sectionName="Secure Your Slot" defaultValues={{
                name: "",
                email: "",
                phone: "",
                organization: "",
                details: ""
              }} submitText="Initiate Project" successMessage="Brief received. Our production team will review your vision and contact you within 24 hours.">
                  <InquiryForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </div>
    </section>;
}